import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiList } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  Notice,
  Pager,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';
import { formatMinor } from '../../lib/money';
import { useCurrency, type Hostel, type Message, type Person, type Room } from './shared';

interface Allocation {
  id: string;
  studentName: string;
  admissionNumber: string;
  hostelName: string;
  roomNumber: string;
  monthlyFeeMinor: number;
  startDate: string;
}

export function ResidentsTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const currency = useCurrency(schoolId);
  const allocating = can('hostel.allocate', schoolId);
  const [hostelId, setHostelId] = useState('');
  const [roomId, setRoomId] = useState('');
  const [search, setSearch] = useState('');
  const [person, setPerson] = useState<Person | null>(null);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [message, setMessage] = useState<Message>(null);

  const hostels = useQuery({
    queryKey: ['hostel', schoolId, 'hostels'],
    queryFn: () =>
      apiList<Hostel>(`/schools/${schoolId}/hostel/hostels`, { query: { pageSize: 100 } }),
  });
  const detail = useQuery({
    queryKey: ['hostel', schoolId, 'hostel', hostelId],
    queryFn: () => api<{ rooms: Room[] }>(`/schools/${schoolId}/hostel/hostels/${hostelId}`),
    enabled: Boolean(hostelId),
  });
  const matches = useQuery({
    queryKey: ['hostel', schoolId, 'students', search],
    queryFn: () =>
      apiList<Person>('/students', { query: { schoolId, q: search, pageSize: 6 } }).then(
        (r) => r.data,
      ),
    enabled: allocating && search.trim().length >= 2 && !person,
  });
  const residents = useQuery({
    queryKey: ['hostel', schoolId, 'residents', filter, page],
    queryFn: () =>
      apiList<Allocation>(`/schools/${schoolId}/hostel/allocations`, {
        query: { status: 'active', hostelId: filter || undefined, page, pageSize: 20 },
      }),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['hostel', schoolId] });
  const fail = (e: unknown) => setMessage({ kind: 'error', text: errorMessage(e) });
  const rooms = (detail.data?.rooms ?? []).filter((r) => r.status === 'active');
  const chosenRoom = roomId || rooms[0]?.id || '';
  const allocate = useMutation({
    mutationFn: () =>
      api(`/schools/${schoolId}/hostel/allocations`, {
        method: 'POST',
        body: { studentId: person!.id, roomId: chosenRoom },
      }),
    onSuccess: () => {
      setMessage({ kind: 'ok', text: t('ho.bedGiven') });
      setPerson(null);
      setSearch('');
      refresh();
    },
    onError: fail,
  });
  const end = useMutation({
    mutationFn: (id: string) =>
      api(`/schools/${schoolId}/hostel/allocations/${id}/end`, { method: 'POST', body: {} }),
    onSuccess: () => {
      setMessage(null);
      refresh();
    },
    onError: fail,
  });

  return (
    <section aria-label={t('ho.tab.residents')} className="space-y-6">
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      {allocating && (
        <form
          noValidate
          aria-label={t('ho.giveBed')}
          className="grid items-end gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (person && chosenRoom) allocate.mutate();
          }}
        >
          <div className="relative">
            <TextField
              label={t('ho.findStudent')}
              value={person ? `${person.firstName} ${person.lastName}` : search}
              onChange={(e) => {
                setPerson(null);
                setSearch(e.target.value);
              }}
            />
            {!person && matches.data && matches.data.length > 0 && (
              <ul className="absolute z-10 mt-1 w-full divide-y divide-slate-100 rounded border border-slate-200 bg-white text-sm shadow">
                {matches.data.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      className="w-full p-2 text-left hover:bg-slate-50"
                      onClick={() => setPerson(p)}
                    >
                      {p.firstName} {p.lastName} · {p.admissionNumber}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <label htmlFor="bed-hostel" className="block text-sm font-medium">
              {t('ho.hostel')}
            </label>
            <select
              id="bed-hostel"
              className={selectClass}
              value={hostelId}
              onChange={(e) => {
                setHostelId(e.target.value);
                setRoomId('');
              }}
            >
              <option value="">{t('ho.choose')}</option>
              {hostels.data?.data
                .filter((h) => h.status === 'active')
                .map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.name} ({t(`ho.residents.${h.residents}` as MessageKey)})
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label htmlFor="bed-room" className="block text-sm font-medium">
              {t('ho.room')}
            </label>
            <select
              id="bed-room"
              className={selectClass}
              value={chosenRoom}
              disabled={!hostelId}
              onChange={(e) => setRoomId(e.target.value)}
            >
              {rooms.map((r) => (
                <option key={r.id} value={r.id}>
                  {t('ho.roomLabel', { number: r.roomNumber })} (
                  {t('ho.bedsTaken', { taken: r.occupied, total: r.capacity })})
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            className={primaryButton}
            disabled={!person || !chosenRoom || allocate.isPending}
          >
            {t('ho.giveBed')}
          </button>
        </form>
      )}

      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">{t('ho.residentsTitle')}</h2>
          <div>
            <label htmlFor="res-filter" className="mr-2 text-sm">
              {t('ho.hostel')}
            </label>
            <select
              id="res-filter"
              className={selectClass}
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">{t('ho.allHostels')}</option>
              {hostels.data?.data.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        {residents.data?.data.length === 0 && (
          <p className="text-slate-600">{t('ho.noResidents')}</p>
        )}
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
          {residents.data?.data.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
              <span>
                <span className="font-medium">{r.studentName}</span> · {r.admissionNumber} ·{' '}
                {r.hostelName}, {t('ho.roomLabel', { number: r.roomNumber })} ·{' '}
                {t('ho.perMonth', { amount: formatMinor(r.monthlyFeeMinor, currency) })}
              </span>
              {allocating && (
                <button
                  type="button"
                  className={secondaryButton}
                  aria-label={`${t('ho.endBed')}: ${r.studentName}`}
                  disabled={end.isPending}
                  onClick={() =>
                    window.confirm(t('ho.confirmEnd', { name: r.studentName })) && end.mutate(r.id)
                  }
                >
                  {t('ho.endBed')}
                </button>
              )}
            </li>
          ))}
        </ul>
        {residents.data && residents.data.meta.totalPages > 1 && (
          <Pager page={page} totalPages={residents.data.meta.totalPages} onPage={setPage} />
        )}
      </div>
    </section>
  );
}
